import { FINANCE_CONFIG } from './config.js';
import { addDaysUTC, createUTCDate, parseDateUTC, toDateKeyUTC } from './dates.js';
import { getEntityId } from './entities.js';
import { getCurrentWalletBalance, toProjectionMovement } from './ledger.js';
import { occursOnDate } from './recurrence.js';
/**
 * Projects each wallet forward from its current balance. Historical movements are
 * intentionally not replayed because the supplied wallet balances already describe today.
 */
export function calculateForwardWalletProjection(transactions, options) {
    const start = parseDateUTC(options.today);
    const end = parseDateUTC(options.targetDate);
    if (end < start)
        throw new RangeError('targetDate must be on or after today');
    return calculateWalletProjectionRange(transactions, options.wallets, start, end, options);
}
/**
 * Replays the ledger up to a concrete date. Callers that need transaction-only
 * balances must pass wallets with a zero balance; this keeps opening balances an
 * explicit choice instead of silently manufacturing money.
 */
export function calculateWalletBalanceSnapshot(transactions, options) {
    const end = parseDateUTC(options.targetDate);
    const requestedStart = options.startDate ? parseDateUTC(options.startDate) : null;
    const firstTransactionDate = transactions.reduce((earliest, transaction) => {
        if (transaction.date === null || transaction.date === undefined)
            return earliest;
        const date = parseDateUTC(transaction.date);
        return earliest === null || date < earliest ? date : earliest;
    }, null);
    const start = requestedStart ?? firstTransactionDate;
    const initialWalletBalances = getInitialWalletBalances(options.wallets);
    if (start === null || start > end) {
        return createWalletPoint(end, [], initialWalletBalances);
    }
    return calculateWalletProjectionRange(transactions, options.wallets, start, end, options).points.at(-1);
}
function calculateWalletProjectionRange(transactions, wallets, start, end, options) {
    const initialWalletBalances = getInitialWalletBalances(wallets);
    const balances = { ...initialWalletBalances };
    const points = [];
    for (let date = start; date <= end; date = addDaysUTC(date, 1)) {
        const dailyTransactions = transactions
            .filter((transaction) => occursOnDate(transaction, date, options))
            .toSorted((left, right) => eventPriority(left.type) - eventPriority(right.type));
        const events = dailyTransactions.map((transaction) => applyTransaction(transaction, date, balances));
        points.push(createWalletPoint(date, events, balances));
    }
    return { initialWalletBalances, points, wallets };
}
export function getDailyWalletProjection(transactions, options) {
    const minYear = options.minYear ?? FINANCE_CONFIG.projection.minYear;
    const maxYear = options.maxYear ?? FINANCE_CONFIG.projection.maxYear;
    assertYearRange(minYear, maxYear);
    const start = createUTCDate(minYear, 1, 1);
    const end = createUTCDate(maxYear, 12, 31);
    return calculateWalletProjectionRange(transactions, options.wallets, start, end, options);
}
function getInitialWalletBalances(wallets) {
    return Object.fromEntries(wallets.map((wallet) => [wallet.id, getCurrentWalletBalance([wallet])]));
}
function createWalletPoint(date, events, balances) {
    return {
        date: toDateKeyUTC(date),
        events,
        globalBalance: Object.values(balances).reduce((sum, balance) => sum + balance, 0),
        walletBalances: { ...balances },
    };
}
function applyTransaction(transaction, date, balances) {
    const movement = toProjectionMovement(transaction, date);
    const walletId = movement.walletId ?? 'unassigned';
    const walletBalanceBefore = balances[walletId] ?? 0;
    const direction = transaction.type === 'income' ? 1 : -1;
    const balanceAfter = walletBalanceBefore + movement.amount * direction;
    balances[walletId] = balanceAfter;
    if (transaction.type === 'transfer') {
        const toWalletId = getEntityId(transaction.toWallet) ?? 'unassigned';
        const toWalletBalanceAfter = (balances[toWalletId] ?? 0) + movement.amount;
        balances[toWalletId] = toWalletBalanceAfter;
        return { ...movement, balanceAfter, toWalletBalanceAfter, walletBalanceBefore };
    }
    return { ...movement, balanceAfter, walletBalanceBefore };
}
function eventPriority(type) {
    if (type === 'income')
        return 0;
    if (type === 'transfer')
        return 1;
    return 2;
}
function assertYearRange(minYear, maxYear) {
    if (!Number.isInteger(minYear) || !Number.isInteger(maxYear) || minYear > maxYear) {
        throw new RangeError('Wallet projection requires a valid year range');
    }
}
//# sourceMappingURL=walletProjection.js.map